import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    AppstoreOutlined,
    ArrowDownOutlined,
    BarChartOutlined,
    CloudOutlined,
    HddOutlined,
    SafetyOutlined,
    TeamOutlined,
    ToolOutlined,
    WifiOutlined,
} from "@ant-design/icons";
import { BudgetTabs } from "../layout/BudgetTabs";
import { StatusTag } from "../components/StatusTag";
import { useBudget } from "../BudgetContext";
import { ImportDay, importTimeline, importedRecords } from "../model/api";
import { CategoryKey, DataSource, usd } from "../model/types";
import { WPC_PROJECTS, WpcProject, projectPath } from "../model/projects";
import { formatCompact, formatMoney } from "../model/format";
import classes from "./SourcesView.module.less";

const CATEGORY_ICON: Record<CategoryKey, React.ReactNode> = {
    saas: <AppstoreOutlined />,
    infra_cloud: <CloudOutlined />,
    hardware: <HddOutlined />,
    telecom: <WifiOutlined />,
    prof_services: <TeamOutlined />,
    security: <SafetyOutlined />,
    data_analytics: <BarChartOutlined />,
    support_maint: <ToolOutlined />,
};

const recordNoun = (s: DataSource, plural: boolean): string =>
    s.kind === "po_feed"
        ? plural
            ? "POs"
            : "PO"
        : plural
        ? "contracts"
        : "contract";

export const SourcesView: React.FC = () => {
    const { workspace } = useBudget();
    const [selectedId, setSelectedId] = useState(workspace.sources[0]?.id);

    const source =
        workspace.sources.find((s) => s.id === selectedId) ?? workspace.sources[0];

    return (
        <>
            <BudgetTabs />

            <div className={classes.layout}>
                <aside className={classes.sidebar}>
                    <div className={classes.sidebarHead}>Sources</div>
                    {workspace.sources.map((s) => (
                        <button
                            key={s.id}
                            type="button"
                            className={`${classes.sidebarItem} ${
                                s.id === source?.id ? classes.sidebarItemSelected : ""
                            }`}
                            onClick={() => setSelectedId(s.id)}
                        >
                            <span
                                className={classes.logo}
                                style={{ background: s.logoColor }}
                            >
                                {s.logoLabel}
                            </span>
                            <span className={classes.sidebarName}>{s.name}</span>
                            <span
                                className={`${classes.dot} ${
                                    s.live ? classes.connected : classes.batch
                                }`}
                            />
                        </button>
                    ))}
                </aside>

                {source && <SourceDetail source={source} />}
            </div>
        </>
    );
};

const SourceDetail: React.FC<{ source: DataSource }> = ({ source }) => {
    if (source.kind === "project") return <ProjectSourceDetail source={source} />;
    return <ImportSourceDetail source={source} />;
};

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

const ProjectSourceDetail: React.FC<{ source: DataSource }> = ({ source }) => {
    const totalTcv = sum(WPC_PROJECTS.map((p) => p.tcv.amount));
    const totalSavings = sum(WPC_PROJECTS.map((p) => p.savingsExpected.amount));

    return (
        <section className={classes.detail}>
            <div className={classes.detailHead}>
                <div className={classes.detailHeadMain}>
                    <span
                        className={classes.detailLogo}
                        style={{ background: source.logoColor }}
                    >
                        {source.logoLabel}
                    </span>
                    <div>
                        <div className={classes.detailName}>{source.name}</div>
                        <div className={classes.detailSub}>{source.detail}</div>
                    </div>
                </div>
            </div>

            <div className={classes.stats}>
                <Stat label="Status">
                    <StatusTag tone="green">Connected</StatusTag>
                </Stat>
                <Stat label="Projects" value={WPC_PROJECTS.length.toString()} />
                <Stat label="TCV covered" value={formatCompact(usd(totalTcv))} />
                <Stat
                    label="Identified savings"
                    value={formatCompact(usd(totalSavings))}
                />
            </div>

            <div className={classes.projectList}>
                {WPC_PROJECTS.map((p) => (
                    <ProjectRow key={p.projectId} project={p} />
                ))}
            </div>
        </section>
    );
};

const ProjectRow: React.FC<{ project: WpcProject }> = ({ project: p }) => (
    <Link className={classes.projectRow} to={projectPath(p)}>
        {p.logoUrl ? (
            <img className={classes.projectLogo} src={p.logoUrl} alt="" loading="lazy" />
        ) : (
            <span className={classes.projectIcon}>
                {p.categoryKey ? CATEGORY_ICON[p.categoryKey] : null}
            </span>
        )}
        <div className={classes.projectBody}>
            <div className={classes.projectName}>
                {p.name}
                <span className={classes.projectVendor}>{p.vendorName}</span>
            </div>
            <div className={classes.projectCat}>{p.category}</div>
        </div>
        <div className={classes.projectMetric}>
            <span className={classes.projectMetricLabel}>TCV</span>
            {formatCompact(p.tcv)}
        </div>
        <div className={classes.projectMetric}>
            <span className={classes.projectMetricLabel}>Savings</span>
            {formatCompact(p.savingsExpected)}
        </div>
    </Link>
);

const ImportSourceDetail: React.FC<{ source: DataSource }> = ({ source }) => {
    const { workspace } = useBudget();
    const records = importedRecords(workspace, source);
    const timeline = importTimeline(source, records);
    const noun = recordNoun(source, records.length !== 1);
    const verb = source.live ? "imported" : "uploaded";

    return (
        <section className={classes.detail}>
            <div className={classes.detailHead}>
                <div className={classes.detailHeadMain}>
                    <span
                        className={classes.detailLogo}
                        style={{ background: source.logoColor }}
                    >
                        {source.logoLabel}
                    </span>
                    <div>
                        <div className={classes.detailName}>{source.name}</div>
                        <div className={classes.detailSub}>{source.detail}</div>
                    </div>
                </div>
            </div>

            <div className={classes.stats}>
                <Stat label="Status">
                    {source.live ? (
                        <StatusTag tone="green">Connected</StatusTag>
                    ) : (
                        <StatusTag tone="blue">Batch upload</StatusTag>
                    )}
                </Stat>
                <Stat
                    label={source.live ? "Last sync" : "Last upload"}
                    value={source.lastSyncedLabel}
                />
                <Stat
                    label={`${noun.charAt(0).toUpperCase()}${noun.slice(1)} ${verb}`}
                    value={records.length.toLocaleString()}
                />
                <Stat
                    label="Spend covered"
                    value={formatMoney(source.spendDollarsCovered)}
                />
            </div>

            <Timeline timeline={timeline} />
        </section>
    );
};

const Timeline: React.FC<{ timeline: ImportDay[] }> = ({ timeline }) => (
    <div className={classes.eventsList}>
        {timeline.map((day) => (
            <div key={day.offset} className={classes.dayGroup}>
                <div className={classes.dayHeader}>
                    {day.relativeLabel ?? day.dateLabel}
                </div>
                {day.rows.map(({ contract: c, timeLabel }) => (
                    <div key={c.id} className={classes.event}>
                        <span className={classes.eventDir}>
                            <ArrowDownOutlined />
                        </span>
                        <span className={classes.eventWhen}>{timeLabel}</span>
                        <div className={classes.eventBody}>
                            <div className={classes.eventTitle}>{c.vendorName}</div>
                            <div className={classes.eventDetail}>{c.categoryName}</div>
                        </div>
                        <span className={classes.eventAmount}>
                            {formatMoney(c.annualSpend)}
                        </span>
                    </div>
                ))}
            </div>
        ))}
    </div>
);

const Stat: React.FC<{
    label: string;
    value?: string;
    children?: React.ReactNode;
}> = ({ label, value, children }) => (
    <div className={classes.stat}>
        <div className={classes.statLabel}>{label}</div>
        <div className={classes.statValue}>{children ?? value}</div>
    </div>
);
